import {
  AUTO_ROUTE_ENTRIES,
  type AutoRouteEntry,
  type AutoRouteId,
} from "#app/config/auto-routes.ts";

export type { AutoRouteId };

export interface AutoRoute {
  name: string;
  slug: string;
  path: string;
  title: string;
  description?: string;
  autoRouteId: AutoRouteId;
  isIndex: boolean;
}

export interface GetAutoRoutesOptions {
  includeIndex?: boolean;
}

interface TemplateModuleExports {
  title?: string;
  description?: string;
  metadata?: {
    title?: string;
    description?: string;
  };
}

/**
 * Static build-time scanning of all template files under app/templates/
 */
const templateModules = import.meta.glob<TemplateModuleExports>("../templates/**/*.gts", {
  eager: true,
});

function humanize(str: string): string {
  return str
    .split(/[-_]/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * Normalizes template paths for auto route matching.
 */
function normalizePath(path: string): string {
  return path.replace(/^(\.\.\/)+templates\//, "").replace(/\.gts$/, "");
}

/**
 * Discovers and returns auto-routes for a specified auto route id.
 */
export function getAutoRoutes(
  autoRouteId: AutoRouteId,
  options: GetAutoRoutesOptions = {},
): AutoRoute[] {
  const autoRoute = AUTO_ROUTE_ENTRIES.find((s) => s.id === autoRouteId);
  if (!autoRoute) {
    return [];
  }

  const routes: AutoRoute[] = [];
  const autoRoutePrefix = `${autoRoute.path}/`;

  for (const [rawPath, mod] of Object.entries(templateModules)) {
    const relativePath = normalizePath(rawPath);
    if (!relativePath.startsWith(autoRoutePrefix)) {
      continue;
    }

    const subPath = relativePath.slice(autoRoutePrefix.length);
    // Ignore deep sub-directory files if they contain slashes
    if (subPath.includes("/")) {
      continue;
    }

    const filename = subPath;
    const isIndex = filename === "index";
    const slug = filename;
    const routeName = isIndex ? `${autoRoute.id}.index` : `${autoRoute.id}.${slug}`;
    const routePath = isIndex ? `/${autoRoute.path}` : `/${autoRoute.path}/${slug}`;

    const title = mod.title ?? mod.metadata?.title ?? (isIndex ? "Index" : humanize(slug));
    const description = mod.description ?? mod.metadata?.description;

    if (isIndex && !options.includeIndex) {
      continue;
    }

    routes.push({
      name: routeName,
      slug,
      path: routePath,
      title,
      description,
      autoRouteId: autoRoute.id,
      isIndex,
    });
  }

  return routes.sort((a, b) => {
    return a.title.localeCompare(b.title);
  });
}

/**
 * Returns a mapping of auto route info and child route slugs for router registration.
 */
export function getAutoRoutesForRouter(): Array<{
  autoRouteEntry: AutoRouteEntry;
  childSlugs: string[];
}> {
  return AUTO_ROUTE_ENTRIES.map((entry) => {
    const routes = getAutoRoutes(entry.id, { includeIndex: false });
    const childSlugs = routes
      .filter((r) => !r.isIndex)
      .map((r) => r.slug)
      .sort();

    return {
      autoRouteEntry: entry,
      childSlugs,
    };
  });
}

export interface RouteDSL {
  route(
    name: string,
    options?: { path?: string; resetNamespace?: boolean } | ((this: RouteDSL) => void),
    callback?: (this: RouteDSL) => void,
  ): void;
}

export interface BuildAutoRoutesOptions {
  resetNamespace?: boolean;
}

/**
 * Registers all auto-discovered routes onto an Ember Router DSL context.
 */
export function buildAutoRoutes(dsl: RouteDSL, options: BuildAutoRoutesOptions = {}): void {
  const resetNamespace = options.resetNamespace ?? true;
  for (const { autoRouteEntry: entry, childSlugs } of getAutoRoutesForRouter()) {
    console.groupCollapsed(`[auto-routes] Adding ${entry.id} routes`);
    const routeOptions: { path: string; resetNamespace?: boolean } = { path: entry.path };
    if (resetNamespace) {
      routeOptions.resetNamespace = true;
    }
    dsl.route(entry.id, routeOptions, function (this: RouteDSL) {
      for (const slug of childSlugs) {
        console.log(`route '${entry.id}.${slug}'`);
        this.route(slug);
      }
    });
    console.groupEnd();
  }
}
